import { Confirm } from '@coop/components';
import { useCallback, useEffect } from 'react';

import useModal from '../../../hooks/useModal';
import { useAppDispatch, useAppSelector } from '../../../hooks/useThunkDispatch';
import { useTimeExpirationWithAsyncEnsure } from '../../../hooks/useTimeExpiration';
import { cartSelectors } from '../../../selectors/cartSelectors';
import { storeSelectors } from '../../../selectors/storeSelectors';
import { TimeslotModalType } from '../../../store/structureDefinitions/modalState';
import { DeliveryMode } from '../../../store/structureDefinitions/storeState';
import { cartThunks } from '../../../thunks/cartThunks';
import { ga4CncTracking } from '../../../tracking/ga4/cnc';

interface TimeslotExpirationDialogProps {
    handleConfirm?: () => void;
}

const TimeslotExpirationDialog = (
    props: React.PropsWithChildren<TimeslotExpirationDialogProps>,
) => {
    const dispatch = useAppDispatch();

    const {
        isOpen: showDialog,
        open: openDialog,
        close: closeDialog,
    } = useModal(TimeslotModalType.TimeslotExpiration);

    const onExpireCallback = useCallback(() => {
        openDialog();
    }, [openDialog]);

    const milisecondsToExpire = useAppSelector((state) => state.cart.timeslot?.milisecondsToExpire);

    const asyncEnsureCall = useCallback(() => dispatch(cartThunks.getCartSilent()), [dispatch]);

    useTimeExpirationWithAsyncEnsure(
        milisecondsToExpire || null,
        asyncEnsureCall,
        onExpireCallback,
    );

    const deliveryMode = useAppSelector(storeSelectors.selectedDeliveryModeOrDefault);
    const store = useAppSelector(storeSelectors.currentStore);
    const postalCode = useAppSelector(cartSelectors.postalCode);
    const storeId = store?.id;
    const storeName = store?.name;

    useEffect(() => {
        if (showDialog) {
            if (deliveryMode === DeliveryMode.homedelivery && postalCode) {
                ga4CncTracking.timeLost.home(postalCode);
            }

            if (deliveryMode === DeliveryMode.pickup && storeId && storeName) {
                ga4CncTracking.timeLost.pickup(storeId, storeName);
            }
        }
    }, [deliveryMode, postalCode, showDialog, storeId, storeName]);

    return (
        <Confirm
            show={showDialog}
            heading="Din reserverade tid har löpt ut"
            message="Du har varit inaktiv i mer än 15 minuter. Varorna finns fortfarande kvar i din varukorg."
            confirmButtonText="Reservera ny tid"
            onConfirm={() => {
                closeDialog();
                if (props.handleConfirm) {
                    props.handleConfirm();
                }
            }}
            close={closeDialog}
        />
    );
};

export default TimeslotExpirationDialog;

// EFFECT FOR TESTING EXPIRATON DIALOG, paste that into TimeslotExpirationDialog
// const testHandle = useRef(null);
// useEffect(() => {
//     testHandle.current = setTimeout(() => {
//         onExpireCallback();
//     }, 5000);

//     return () => {
//         clearTimeout(testHandle.current);
//     };
// }, [testHandle, onExpireCallback]);
